import React from "react";
import { Link } from "react-router-dom";
import {
  FileSignature,
  MessagesSquare,
  Scale,
  ShieldAlert,
  Sparkles,
  ArrowRight,
  Cpu,
  FileCheck2,
  Microscope,
  Fingerprint,
  Users2,
  Globe2,
  GitBranchPlus,
  Pill,
  BrainCircuit,
  Lock,
  ClipboardList,
  Activity,
  ShieldCheck,
  Beaker,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { LANDING } from "@/constants/testIds";

const CAPABILITIES = [
  {
    code: "01",
    icon: FileSignature,
    tag: "SUBMISSION DRAFTING",
    title: "510(k), PMA, De Novo, IND, NDA & ANDA workflows",
    desc: "Structured drafting for the entire premarket dossier — device and drug — with 21 CFR / ICH section maps, substantial-equivalence tables, and inline citations. Every draft is a working artifact your team can edit, redline, and export.",
  },
  {
    code: "02",
    icon: BrainCircuit,
    tag: "REGULATORY INTELLIGENCE",
    title: "Context-aware regulatory chat",
    desc: "Ask 21 CFR 820, ISO 14971, IEC 62304, ICH Q8-Q11, or a specific guidance document. Answers stream back grounded in your device or therapeutic context, with citations you can trace to source.",
  },
  {
    code: "03",
    icon: Scale,
    tag: "PREDICATE INTELLIGENCE",
    title: "Ranked predicate & RLD analysis",
    desc: "Semantic scoring across openFDA's live 510(k) database and Reference Listed Drugs. Save predicate sets per project, generate side-by-side substantial-equivalence write-ups in seconds.",
  },
  {
    code: "04",
    icon: ShieldAlert,
    tag: "POST-MARKET SURVEILLANCE",
    title: "MAUDE + FAERS adverse-event radar",
    desc: "17M+ device MDR reports and 20M+ drug FAERS events — pulled live per device family or drug class. Get synthesized failure-mode briefs and risk signals you can hand to your PMS lead.",
  },
  {
    code: "05",
    icon: Fingerprint,
    tag: "IP & FREEDOM-TO-OPERATE",
    title: "Live USPTO patent screening",
    desc: "USPTO ODP-backed screening surfaces overlapping US patents, generates an FTO briefing, and recommends concrete design-arounds. Screening only — a registered patent attorney signs off on the opinion.",
  },
  {
    code: "06",
    icon: Beaker,
    tag: "CLINICAL EVIDENCE",
    title: "ClinicalTrials.gov, RLD labels & CMC",
    desc: "500K+ studies on ClinicalTrials.gov, full Prescribing Information via openFDA, and Module 3 CMC scaffolding grounded in ICH M4Q — search evidence you can cite in a CER, IND, or ANDA.",
  },
  {
    code: "07",
    icon: Users2,
    tag: "TEAM WORKSPACES",
    title: "Project spaces built for RA, QA & engineering",
    desc: "Bundle every artifact for a device or therapeutic into one project. Invite RA specialists, QA leads, quality engineers, and clinical writers. Track cited-regulation freshness against eCFR. One-click submission-package PDF.",
  },
  {
    code: "08",
    icon: GitBranchPlus,
    tag: "CHANGE CONTROL",
    title: "Post-market change decisions in minutes",
    desc: "FDA's 2017 change-control decision tree applied to any change. Letter-to-File vs. Special 510(k) recommendation with rationale and DHF documentation checklist.",
  },
  {
    code: "09",
    icon: Pill,
    tag: "THERAPEUTIC DOSSIERS",
    title: "IND, NDA, ANDA, Label & CMC drafting",
    desc: "Full CDER coverage: 21 CFR 312 IND, NDA, ANDA, Prescribing Information per 21 CFR 201.57, Module 3 CMC per ICH M4Q, OTC monograph work. Grounded in openFDA RLD, FAERS, and label data.",
  },
];

const AUDIENCES = [
  { title: "Regulatory Affairs", desc: "Draft submissions, own the citations, ship faster.", icon: FileSignature },
  { title: "Founders & Startups", desc: "Get from concept to premarket path without an in-house RA team.", icon: Sparkles },
  { title: "Quality & QA leads", desc: "Design Controls, DHF, CAPA, and audit-ready QMS scaffolding.", icon: ShieldCheck },
  { title: "R&D & Engineering", desc: "Turn requirements into DHF, risk files, and V&V plans.", icon: Cpu },
  { title: "Clinical & Medical Affairs", desc: "CERs, PMCF plans, IND CMC narratives — evidence-grounded.", icon: Microscope },
  { title: "Consultancies", desc: "Handle 3× the client load with one shared workspace per program.", icon: Users2 },
];

const WORKFLOWS = [
  { code: "IFU", label: "Instructions for Use", meta: "21 CFR 801" },
  { code: "DHF", label: "Design History File", meta: "820.30(j)" },
  { code: "RMF", label: "Risk Management File", meta: "ISO 14971" },
  { code: "DC", label: "Design Controls SOP", meta: "820.30" },
  { code: "CYB", label: "Cybersecurity Docs", meta: "2023 Guidance" },
  { code: "SE", label: "Substantial Equivalence", meta: "807.87(f)" },
  { code: "IND", label: "IND Package", meta: "21 CFR 312" },
  { code: "NDA", label: "New Drug Application", meta: "21 CFR 314" },
  { code: "ANDA", label: "Abbreviated NDA", meta: "21 CFR 314.94" },
  { code: "CMC", label: "Module 3 CMC", meta: "ICH M4Q" },
  { code: "PI", label: "Prescribing Info", meta: "21 CFR 201.57" },
  { code: "CER", label: "Clinical Evaluation", meta: "MEDDEV 2.7/1" },
];

const STANDARDS = [
  "21 CFR 820 QSR",
  "21 CFR 807 · 814 · 860.200",
  "21 CFR 312 · 314",
  "ICH M4Q / Q8-Q11",
  "ISO 13485 · ISO 14971",
  "IEC 62304 · IEC 62366",
  "ISO 10993 Biocomp",
  "MDR 2017/745 · MEDDEV 2.7/1",
  "eCFR live sync",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 grain">
        <div className="relative z-10 mx-auto grid max-w-[1400px] gap-12 px-6 py-24 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:py-32">
          <div className="lg:col-span-8">
            <div className="mb-8 inline-flex items-center gap-3 border border-slate-200 px-3 py-1.5">
              <span className="h-1.5 w-1.5 bg-[#2563EB]"></span>
              <span className="overline text-slate-600">CDRH · CDER · 21 CFR · ICH · EU MDR</span>
            </div>

            <h1 className="font-serif text-5xl font-semibold leading-[1.02] tracking-tight sm:text-6xl lg:text-[88px]">
              The <span className="text-slate-600">FDA</span> regulatory
              <br />
              workspace for
              <br />
              <span className="italic text-slate-600">medical devices</span>
              <br />
              &amp; <span className="italic text-slate-600">therapeutics</span>.
            </h1>

            <p className="mt-10 max-w-2xl text-lg leading-relaxed text-slate-600 lg:text-xl">
              Qualmitta is an end-to-end SaaS platform for medtech and pharma teams — draft
              510(k), PMA, IND, NDA, ANDA, CMC and EU MDR dossiers, mine live openFDA
              intelligence, and run post-market surveillance from a single project workspace.
              <b className="text-slate-900"> One system of record for every submission.</b>
            </p>

            <ul className="mt-8 grid gap-2.5 md:grid-cols-2 lg:max-w-xl">
              {[
                "Draft 510(k) / PMA / IND / NDA / ANDA in minutes",
                "Live openFDA + USPTO + ClinicalTrials.gov + DailyMed + RxNorm",
                "ISO 14971 risk matrix + 21 CFR 820.30 design controls",
                "Audit-ready QMS SOPs + version history + PDF export",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 bg-[#2563EB]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/workspace"
                data-testid={LANDING.ctaStart}
                className="group inline-flex items-center gap-3 bg-[#2563EB] px-6 py-4 text-sm font-semibold tracking-wide text-white transition-all hover:bg-[#1D4ED8]"
              >
                Launch the workspace
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/demo"
                data-testid="hero-book-demo"
                className="inline-flex items-center gap-2 border border-slate-300 bg-white px-6 py-4 text-sm font-medium text-slate-700 hover:border-[#2563EB] hover:text-[#2563EB]"
              >
                Book a demo
              </Link>
              <a
                href="#capabilities"
                data-testid={LANDING.ctaLearn}
                className="inline-flex items-center gap-2 px-2 py-4 text-sm font-medium text-slate-500 hover:text-slate-900"
              >
                See the platform ↓
              </a>
            </div>

            <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 overline text-slate-500">
              <span>End-to-end.</span>
              <span className="h-1 w-1 bg-slate-200" />
              <span>Audit-ready.</span>
              <span className="h-1 w-1 bg-slate-200" />
              <span>Team-first.</span>
              <span className="h-1 w-1 bg-slate-200" />
              <span>Built for RA, QA & R&D.</span>
            </div>
          </div>

          {/* Tetris side panels */}
          <div className="lg:col-span-4">
            <div className="grid gap-4">
              <div data-testid="hero-stat-openfda" className="border border-slate-200 bg-slate-50 p-6">
                <div className="overline mb-3 text-[#2563EB]">LIVE / openFDA + NLM</div>
                <div className="font-serif text-3xl font-semibold">2M+</div>
                <div className="mt-1 text-sm text-slate-500">
                  510(k) + RLD + Orange/Purple Book + <span className="font-semibold text-slate-700">DailyMed SPL labels</span> + <span className="font-semibold text-slate-700">RxNorm concepts</span>, indexed &amp; scored
                </div>
              </div>
              <div data-testid="hero-stat-maude" className="border border-slate-200 bg-slate-50 p-6">
                <div className="overline mb-3 text-[#2563EB]">MAUDE + FAERS</div>
                <div className="font-serif text-3xl font-semibold">37M+</div>
                <div className="mt-1 text-sm text-slate-500">Adverse-event reports across devices and therapeutics</div>
              </div>
              <div data-testid="hero-stat-chuck" className="border border-slate-200 bg-slate-50 p-6">
                <div className="overline mb-3 text-[#2563EB]">MODEL</div>
                <div className="font-serif text-xl font-semibold">Chuck + Claude Sonnet 4.5</div>
                <div className="mt-1 text-sm text-slate-500">
                  Regulatory-tuned reasoning purpose-built for CDRH & CDER workflows
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ticker */}
      <div className="border-b border-slate-200 py-4 overflow-hidden">
        <div className="marquee-track overline text-slate-400">
          {Array(2).fill(null).map((_, i) => (
            <React.Fragment key={i}>
              <span>21 CFR 820 QSR</span><span>·</span>
              <span>21 CFR 807 510(k)</span><span>·</span>
              <span>21 CFR 814 PMA</span><span>·</span>
              <span>21 CFR 312 IND</span><span>·</span>
              <span>21 CFR 314 NDA/ANDA</span><span>·</span>
              <span>ICH M4Q · Q8 · Q9 · Q10 · Q11</span><span>·</span>
              <span>ISO 14971:2019</span><span>·</span>
              <span>ISO 13485</span><span>·</span>
              <span>IEC 62304</span><span>·</span>
              <span>IEC 62366 Usability</span><span>·</span>
              <span>ISO 10993 Biocomp</span><span>·</span>
              <span>21 CFR 803 MDR</span><span>·</span>
              <span>21 CFR 201.57 PI</span><span>·</span>
              <span>DailyMed SPL (NLM)</span><span>·</span>
              <span>RxNorm (NLM)</span><span>·</span>
              <span>MDR 2017/745</span><span>·</span>
              <span>Cybersecurity Premarket 2023</span><span>·</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* MEET CHUCK */}
      <section id="chuck" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="overline mb-4 text-[#2563EB]">MEET CHUCK</div>
              <h2 className="font-serif text-4xl font-semibold leading-tight lg:text-5xl">
                A regulatory model,
                <br />
                <span className="italic text-slate-600">built where the work happens.</span>
              </h2>
              <p className="mt-6 text-slate-600">
                Chuck is Qualmitta's in-house language model, specialized for FDA and EU MDR
                workflows. It learns from expert-graded regulatory drafts, curated guidance
                corpora, and structured knowledge from openFDA, USPTO, and eCFR. Today Chuck
                runs alongside Claude Sonnet 4.5 — over time, more of your workspace will be
                served by Chuck directly.
              </p>
              <Link
                to="/workspace"
                className="mt-8 inline-flex items-center gap-2 border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 transition-all hover:border-[#2563EB] hover:text-slate-900"
              >
                Try Chuck in the workspace
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="grid gap-px bg-slate-100 md:grid-cols-2">
                <div data-testid="chuck-privacy-optin" className="bg-white p-8">
                  <Lock className="mb-4 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="overline mb-2 text-[#2563EB]">OPT-IN, ALWAYS</div>
                  <h3 className="font-serif text-xl font-semibold">Training is opt-in.</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    Nothing you draft trains Chuck unless you turn it on in your workspace
                    settings. It is off by default for individual accounts and{" "}
                    <b className="text-slate-900">excluded by default for Enterprise tenants</b>.
                  </p>
                </div>
                <div data-testid="chuck-privacy-deid" className="bg-white p-8">
                  <ShieldCheck className="mb-4 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="overline mb-2 text-[#2563EB]">DE-IDENTIFIED</div>
                  <h3 className="font-serif text-xl font-semibold">Stripped before it lands in training.</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    Contributions are aggregated, de-identified, and stripped of proprietary
                    device names, patient data, and confidential identifiers before ever
                    reaching a training corpus.
                  </p>
                </div>
                <div data-testid="chuck-privacy-nothird" className="bg-white p-8">
                  <BrainCircuit className="mb-4 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="overline mb-2 text-[#2563EB]">NO 3RD-PARTY TRAINING</div>
                  <h3 className="font-serif text-xl font-semibold">Your work is not sold to model vendors.</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    We do not share your inputs, outputs, or drafts with third-party model
                    providers for their training. Claude sees only per-session context under
                    Anthropic's zero-retention API terms.
                  </p>
                </div>
                <div data-testid="chuck-privacy-enterprise" className="bg-white p-8">
                  <ClipboardList className="mb-4 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="overline mb-2 text-[#2563EB]">ENTERPRISE-SAFE</div>
                  <h3 className="font-serif text-xl font-semibold">Enterprise tenants are excluded by default.</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    Enterprise workspaces are excluded from Chuck training pipelines unless a
                    program owner explicitly opts in. Full data-use logs are available on
                    request as part of an Enterprise agreement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <div className="mb-16 flex flex-wrap items-end justify-between gap-8">
            <div>
              <div className="overline mb-4 text-slate-500">PLATFORM / {CAPABILITIES.length}</div>
              <h2 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
                Every step of a submission,
                <br />
                <span className="italic text-slate-600">in one workspace.</span>
              </h2>
            </div>
            <p className="max-w-md text-slate-500">
              Purpose-built for RA, QA, R&D, clinical, and consulting teams shipping
              Class I / II / III devices and CDER-regulated drugs.
            </p>
          </div>

          <div className="grid gap-px bg-slate-100 md:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.code}
                  data-testid={LANDING.featureCard}
                  className="group relative bg-[#FAFAF7] p-8 transition-all hover:bg-slate-50 lg:p-12"
                >
                  <div className="mb-10 flex items-start justify-between">
                    <Icon className="h-8 w-8 text-[#2563EB]" strokeWidth={1.5} />
                    <span className="font-mono text-xs text-slate-400">/{c.code}</span>
                  </div>
                  <div className="overline mb-3 text-[#2563EB]">{c.tag}</div>
                  <h3 className="font-serif text-2xl font-semibold leading-tight text-slate-900">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-slate-500">
                    {c.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section id="who" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <div className="mb-16 max-w-3xl">
            <div className="overline mb-4 text-slate-500">WHO IT'S FOR</div>
            <h2 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
              One workspace,{" "}
              <span className="italic text-slate-600">every seat</span> in your regulatory program.
            </h2>
          </div>

          <div className="grid gap-px bg-slate-100 md:grid-cols-2 lg:grid-cols-3">
            {AUDIENCES.map((a) => {
              const I = a.icon;
              return (
                <div key={a.title} data-testid="audience-card" className="bg-white p-8 transition-colors hover:bg-slate-50">
                  <I className="mb-6 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="font-serif text-xl font-semibold text-slate-900">{a.title}</div>
                  <p className="mt-2 text-sm text-slate-500">{a.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WORKFLOWS */}
      <section id="workflows" className="border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <div className="mb-16 max-w-3xl">
            <div className="overline mb-4 text-slate-500">DOCUMENT TYPES</div>
            <h2 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
              From <span className="italic text-slate-600">Design Controls</span> to{" "}
              <span className="italic text-slate-600">Prescribing Information</span>. Covered.
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-px bg-slate-100 md:grid-cols-3 lg:grid-cols-6">
            {WORKFLOWS.map((w) => (
              <div key={w.code} className="bg-[#FAFAF7] p-6 hover:bg-slate-50 transition-colors">
                <div className="font-mono text-2xl font-semibold text-[#2563EB]">
                  {w.code}
                </div>
                <div className="mt-4 text-sm font-medium text-slate-900">{w.label}</div>
                <div className="overline mt-2 text-slate-400">{w.meta}</div>
              </div>
            ))}
          </div>

          <div className="mt-16 grid gap-6 border border-slate-200 bg-slate-50 p-8 lg:grid-cols-3 lg:p-12">
            <div className="lg:col-span-2">
              <div className="overline mb-3 text-[#2563EB]">HOW IT WORKS</div>
              <h3 className="font-serif text-3xl font-semibold leading-tight">
                Spin up a project. Describe the device or therapeutic. Draft, redline, ship.
              </h3>
              <p className="mt-4 text-slate-500">
                Qualmitta structures every artifact to the exact FDA / ICH / EU MDR section
                requirements, cites regulations inline, and exports to PDF or DOCX — with
                full version history and reviewer redlines built in.
              </p>
            </div>
            <div className="flex flex-col justify-end gap-4">
              {[
                { i: Cpu, l: "1. Create a project" },
                { i: FileCheck2, l: "2. Draft & redline in real time" },
                { i: Microscope, l: "3. Assemble & export the package" },
              ].map(({ i: I, l }) => (
                <div key={l} className="flex items-center gap-3 border-l-2 border-[#2563EB] pl-4">
                  <I className="h-4 w-4 text-slate-600" />
                  <span className="text-sm text-slate-700">{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST & CREDIBILITY */}
      <section id="trust" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="overline mb-4 text-[#2563EB]">TRUST &amp; CREDIBILITY</div>
              <h2 className="font-serif text-4xl font-semibold tracking-tight lg:text-5xl">
                Built on the standards
                <br />
                <span className="italic text-slate-600">your reviewers already read.</span>
              </h2>
              <p className="mt-6 text-slate-600">
                Qualmitta cites and cross-references against the same primary sources FDA
                and Notified Body reviewers use. Every regulation reference is a live link
                to eCFR — no hallucinated CFR sections, no stale citations.
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3">
                {STANDARDS.map((s) => (
                  <div key={s} data-testid="standards-chip" className="bg-white p-6 transition-colors hover:bg-slate-50">
                    <Activity className="mb-3 h-4 w-4 text-[#2563EB]" strokeWidth={2} />
                    <div className="font-mono text-sm text-slate-900">{s}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid gap-4 border border-slate-200 bg-slate-50 p-6 md:grid-cols-3">
                <div>
                  <div className="overline mb-2 text-slate-500">DATA SOURCES</div>
                  <div className="text-sm text-slate-900">openFDA · USPTO ODP · ClinicalTrials.gov · DailyMed (NLM) · RxNorm (NLM) · eCFR</div>
                </div>
                <div>
                  <div className="overline mb-2 text-slate-500">RETENTION</div>
                  <div className="text-sm text-slate-900">Zero-retention model calls · Per-tenant DB isolation</div>
                </div>
                <div>
                  <div className="overline mb-2 text-slate-500">AUDIT TRAIL</div>
                  <div className="text-sm text-slate-900">Version history · Redlines · Export logs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-slate-200">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-10 px-6 py-24 lg:flex-row lg:items-center lg:px-10">
          <div>
            <div className="overline mb-3 text-[#2563EB]">START NOW</div>
            <h2 className="font-serif text-4xl font-semibold leading-tight lg:text-6xl">
              Ship your next
              <br /> submission from Qualmitta.
            </h2>
          </div>
          <Link
            to="/workspace"
            data-testid={LANDING.ctaFooter}
            className="group inline-flex items-center gap-3 bg-[#2563EB] px-8 py-5 text-sm font-semibold tracking-wide text-white transition-all hover:bg-[#1D4ED8]"
          >
            <Sparkles className="h-4 w-4" />
            Open the workspace
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-[#FAFAF7]">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-6 py-12 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <div className="font-serif text-3xl font-semibold text-slate-900">
              Qualmitta<span className="text-[#2563EB]">.</span>
            </div>
            <p className="mt-3 max-w-sm text-sm text-slate-500">
              The regulatory workspace for medical device and pharma teams — RA, QA, R&D and clinical, in one system of record.
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="overline mb-3 text-slate-500">GET IN TOUCH</div>
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="group inline-flex items-center gap-2 font-serif text-xl font-semibold text-slate-900 hover:text-[#93C5FD]"
            >
              Contact us
              <span className="inline-block translate-y-[-1px] transition-transform group-hover:translate-x-1">→</span>
            </Link>
            <p className="mt-2 text-sm text-slate-500">
              Questions, feedback, or a regulatory workflow we should support next?
              Send a note at{" "}
              <a href="mailto:hello@qualmitta.com" className="text-slate-600 hover:text-slate-900">hello@qualmitta.com</a>{" "}
              or use the <Link to="/contact" className="text-slate-600 hover:text-slate-900">contact form</Link>.
            </p>
          </div>
          <div className="lg:col-span-3">
            <div className="overline mb-3 text-slate-500">STANDARDS COVERED</div>
            <ul className="space-y-1.5 text-sm text-slate-500">
              <li>21 CFR 820 · 807 · 814 · 312 · 314</li>
              <li>ICH M4Q · Q8-Q11</li>
              <li>ISO 14971 · ISO 13485</li>
              <li>IEC 62304 · IEC 62366</li>
              <li>EU MDR 2017/745 · MEDDEV 2.7/1</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200">
          <div className="mx-auto flex flex-wrap items-center justify-between gap-6 px-6 py-6 lg:px-10 max-w-[1400px]">
            <div className="overline text-slate-400">
              © Qualmitta · Not legal advice · Verify with qualified regulatory counsel
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <Link to="/terms" data-testid="footer-link-terms" className="overline text-slate-500 hover:text-slate-900">
                Terms of Use
              </Link>
              <Link to="/privacy" data-testid="footer-link-privacy" className="overline text-slate-500 hover:text-slate-900">
                Privacy Policy
              </Link>
              <Link to="/disclaimer" data-testid="footer-link-disclaimer" className="overline text-slate-500 hover:text-slate-900">
                Legal Disclaimer
              </Link>
              <Link to="/security" data-testid="footer-link-security" className="overline text-slate-500 hover:text-slate-900">
                Security &amp; Trust
              </Link>
              <span className="overline text-slate-400">v0.4 · Chuck + Claude Sonnet 4.5 + openFDA</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
